/**
 * Spaces along the top (RFC 0281 S5a).
 *
 * THE SAME AFFORDANCES AS THE SIDEBAR ROW, deliberately. Tabs are a place the
 * list is DRAWN, not a reduced version of it: a view where you can switch but
 * not rename, delete or add is not a preference, it is a worse mode, and
 * choosing it in settings would quietly cost you three things nobody mentioned.
 *
 * So: click to switch, double click to rename, × to delete behind one confirm,
 * + to make one. Inline, because that is what this app does everywhere else
 * (`SessionRow`: no modal, no window.confirm).
 *
 * The behaviour behind all of that is `useSpaceActions`, shared with the
 * sidebar's `SpacesView` — what is left in here is layout, which is the only
 * part that actually differs between a row and a tab.
 */

import { Check, Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import { SessionMark } from '../../services/sessions/SessionMark'
import { SpaceDot } from './SpaceDot'
import { type SpaceActivity, spaceSessionIds } from './spaceRows'
import { useSpaceActions } from './useSpaceActions'
import { useSpaceReorder } from './useSpaceReorder'

/**
 * The mark of the first session open in a space, or nothing.
 *
 * A space is not a session and has no mark of its own. Its first session is
 * the work it is for most of the time, and usually the one an unnamed space is
 * labelled by (`spaceLabel` skips a session with no title yet).
 */
function LeadMark({ content }: { content?: string }) {
  const lead = useMemo(
    () => (content ? spaceSessionIds(content)[0] : undefined),
    [content],
  )
  return lead ? <SessionMark id={lead} size={12} /> : null
}

/** The gap a drop would land in, drawn between two tabs. */
function DropGap({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      className={`h-4 w-0.5 shrink-0 rounded-full ${on ? 'bg-blue-500' : 'bg-transparent'}`}
    />
  )
}

export function SpaceTabs({
  spaces,
  currentSpaceId,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  onReorder,
  spaceActivity,
  trailing,
}: {
  /** `content` is the space's pane tree; with it, a tab leads with its first
   *  session's mark (RFC 0311). Optional so an installed header that hands
   *  plain names still draws. */
  spaces: { spaceId: string; name: string; content?: string }[]
  currentSpaceId: string | null
  onSwitch: (id: string) => void
  onCreate: () => void
  onRename: (id: string, name: string) => void
  onDelete: (id: string) => void
  onReorder?: (id: string, toIndex: number) => void
  /** What the sessions open in each space are doing (RFC 0330). */
  spaceActivity?: ReadonlyMap<string, SpaceActivity>
  /** Drawn at the right end of the strip, e.g. the voice call (RFC 0307).
   *  In the flow, so it never sits over anything. */
  trailing?: ReactNode
}) {
  const {
    renaming,
    draft,
    confirming,
    setDraft,
    beginRename,
    commitRename,
    cancelRename,
    askDelete,
    confirmDelete,
    cancelDelete,
  } = useSpaceActions({ onRename, onDelete })
  const reorder = useSpaceReorder({
    spaces,
    axis: 'x',
    ...(onReorder ? { onReorder } : {}),
  })

  // Keep the current space's tab in view when it changes, since the strip
  // can now scroll it out of sight.
  const scroller = useRef<HTMLDivElement>(null)
  useEffect(() => {
    scroller.current
      ?.querySelector(`[data-space-tab="${currentSpaceId}"]`)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [currentSpaceId])

  return (
    <div
      className="flex min-w-0 shrink-0 items-center gap-1 border-border/60 border-b px-2 py-1"
      {...reorder.stripProps}
    >
      {/* The tabs scroll on their own, with no bar, so the new-space button
          and the trailing pill stay put at the end however many spaces there
          are. A mouse wheel scrolls them sideways. */}
      <div
        ref={scroller}
        onWheel={(e) => {
          if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
          e.currentTarget.scrollLeft += e.deltaY
        }}
        style={{ scrollbarWidth: 'none' }}
        className="scrollbar-none flex min-w-0 items-center gap-1 overflow-x-auto"
      >
        {spaces.map((sp, index) => {
          const current = sp.spaceId === currentSpaceId
          const gap = <DropGap on={reorder.isDropGap(index)} />
          if (renaming === sp.spaceId)
            return (
              <Fragment key={sp.spaceId}>
                {gap}
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitRename(sp.spaceId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(sp.spaceId)
                    if (e.key === 'Escape') cancelRename()
                  }}
                  // biome-ignore lint/a11y/noAutofocus: focus follows an explicit rename
                  autoFocus
                  className="w-24 shrink-0 rounded border border-border bg-background px-1 text-xs outline-none focus:border-blue-500"
                />
              </Fragment>
            )
          return (
            <Fragment key={sp.spaceId}>
              {gap}
              <span
                {...reorder.spaceProps(sp.spaceId, index)}
                data-space-tab={sp.spaceId}
                className={`group/tab flex shrink-0 items-center rounded ${
                  current ? 'bg-muted' : ''
                } ${reorder.draggingId === sp.spaceId ? 'opacity-40' : ''}`}
              >
                <button
                  type="button"
                  title={`Switch to ${sp.name} — double click to rename`}
                  onClick={() => onSwitch(sp.spaceId)}
                  onDoubleClick={() => beginRename(sp.spaceId, sp.name)}
                  className={`flex items-center gap-1.5 px-2 py-0.5 text-xs ${
                    current
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <LeadMark content={sp.content} />
                  {sp.name || 'Untitled space'}
                  <SpaceDot activity={spaceActivity?.get(sp.spaceId)} />
                </button>
                {confirming === sp.spaceId ? (
                  <>
                    <button
                      type="button"
                      title="Confirm delete"
                      onClick={() => confirmDelete(sp.spaceId)}
                      className="rounded p-0.5 pr-1 text-red-400"
                    >
                      <Check size={12} />
                    </button>
                    <button
                      type="button"
                      title="Cancel"
                      onClick={cancelDelete}
                      className="rounded p-0.5 pr-1 text-muted-foreground"
                    >
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  // The LAST space has no delete control at all (§7): deleting it
                  // would leave the window with no arrangement to fall back to.
                  spaces.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Delete ${sp.name}`}
                      title="Delete space"
                      onClick={() => askDelete(sp.spaceId)}
                      className="rounded p-0.5 pr-1 text-muted-foreground opacity-0 hover:text-foreground group-hover/tab:opacity-100"
                    >
                      <X size={12} />
                    </button>
                  )
                )}
              </span>
            </Fragment>
          )
        })}
        <DropGap on={reorder.isDropGap(spaces.length)} />
      </div>
      <button
        type="button"
        aria-label="New space"
        title="New space"
        onClick={() => onCreate()}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Plus size={12} />
      </button>
      {trailing ? (
        // Pinned to the right of the strip, past the tabs, so it never reads
        // as another tab; the right padding keeps it clear of the window's
        // status dot in the corner. Inside the strip, so its border runs on
        // under the pill instead of stopping short of it.
        <div className="ml-auto flex shrink-0 items-center pr-5 pl-2">
          {trailing}
        </div>
      ) : null}
    </div>
  )
}
